import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

export function ReserveStockModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [available, setAvailable] = useState(null);
  const [error, setError] = useState(null);

  const warehouses = useQuery({ queryKey: ['inventory', 'warehouses'], queryFn: () => inventoryApi.listWarehouses().then((r) => r.items), enabled: open });
  const products = useQuery({ queryKey: ['inventory', 'products', 'all'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });

  useEffect(() => {
    if (open) { setProductId(''); setWarehouseId(''); setQuantity('1'); setAvailable(null); setError(null); }
  }, [open]);

  // Show available (on-hand minus reserved) for the chosen product + warehouse.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!productId || !warehouseId) { setAvailable(null); return; }
      try {
        const levels = await inventoryApi.stockByProduct(productId).then((r) => r.items);
        const lvl = levels.find((l) => l.warehouseId === warehouseId);
        if (!cancelled) setAvailable(lvl ? lvl.available : '0');
      } catch { if (!cancelled) setAvailable(null); }
    }
    load();
    return () => { cancelled = true; };
  }, [productId, warehouseId]);

  const save = useMutation({
    mutationFn: (body) => inventoryApi.reserveStock(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory', 'reservations'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!productId || !warehouseId || !(Number(quantity) > 0)) { setError(t('res.error.required')); return; }
    save.mutate({ productId, warehouseId, quantity: Number(quantity) });
  }

  const exceeds = available != null && Number(quantity) > Number(available);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('res.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('res.field.product')}</span>
          <select className="input" value={productId} onChange={(e) => setProductId(e.target.value)} required>
            <option value="">-</option>
            {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('res.field.warehouse')}</span>
            <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
              <option value="">-</option>
              {(warehouses.data || []).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('res.field.quantity')}</span>
            <input className="input num" type="number" min="0" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          </label>
        </div>
        {available != null && (
          <div className={`alert ${exceeds ? 'danger' : 'info'}`}>{t('res.field.available', { qty: String(available) })}</div>
        )}
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
